<template>
  <bm-collapsible-card
    :label="$m.crm_activity_log"
    :data-qa="$m.keys.crm_activity_log"
  >
    <bm-form-flow-layout cols="12">
      <bm-tabs v-model="currentTab" :items="tabItems" centered />
      <bm-timeline
        :items="activityLogs"
        :more-items-available="moreEntriesToLoad"
        :max-height="500"
        :icon="(item) => item.association.icon"
        @load-next-items="chunkCount++"
      >
        <template #default="slotProps">
          <activity-log-entry-card :activity-log-entry="slotProps.item" />
        </template>
      </bm-timeline>
    </bm-form-flow-layout>
  </bm-collapsible-card>
</template>

<script lang="ts">
import BmCollapsibleCard from "@/components/controls/card/BmCollapsibleCard.vue";
import BmTabs from "@/components/controls/tabs/BmTabs.vue";
import api from "@/app/api";
import {
  ActivityLogFilter,
  activityLogService,
} from "@/app/services/generated/activitylogService";
import { EnumActivityLogType } from "@/app/services/generated/enums/enumActivityLogType.generated";
import type Guid from "@/app/types/common/guid";
import {
  withLoading,
  wrapPromiseWithErrorHandling,
} from "@/components/composables/componentTools";
import BmFormFlowLayout from "@/components/controls/BmFormFlowLayout.vue";
import type BmTabItem from "@/components/controls/tabs/BmTabItem";
import BmTimeline from "@/components/controls/timeline/BmTimeline.vue";
import { useI18n } from "@/plugins/i18n";
import type { PropType } from "vue";
import { computed, defineComponent, ref, shallowRef, watch } from "vue";
import type { ActivityLogEntryCardType } from "@/components/pages/outlet/crm/subcomponents/activityLogEntryCard";
import { mapActivityLogEntries } from "@/components/pages/outlet/crm/subcomponents/activityLogEntryCard";
import ActivityLogEntryCard from "@/components/pages/outlet/crm/subcomponents/ActivityLogEntryCard.vue";
import { EnumBmEntityType } from "@/app/services/generated/enums/enumBmEntityType.generated";

export enum ActivityLogType {
  outlet,
  lead,
  supplier,
}

export default defineComponent({
  components: {
    BmTimeline,
    BmFormFlowLayout,
    ActivityLogEntryCard,
    BmCollapsibleCard,
    BmTabs,
  },
  name: "ActivityLogCard",
  props: {
    entityId: {
      type: Object as PropType<Guid>,
      required: true,
    },
    logType: {
      type: Number as PropType<ActivityLogType>,
      required: true,
    },
  },
  setup(props) {
    const { $m } = useI18n();
    const activityLogs = shallowRef<readonly ActivityLogEntryCardType[]>([]);
    const currentTab = ref(0);
    const chunkCount = ref(1); // loadNextItems increments by 1 on each call
    const chunkSize = 100; // How many entries to load for each chunk
    const moreEntriesToLoad = ref(true);

    const entityType = computed(() => {
      if (props.logType === ActivityLogType.outlet) {
        return EnumBmEntityType.outlet;
      } else if (props.logType === ActivityLogType.lead) {
        return EnumBmEntityType.lead;
      } else if (props.logType === ActivityLogType.supplier) {
        return EnumBmEntityType.supplier;
      } else {
        return null;
      }
    });

    const tabItems = computed((): BmTabItem[] => [
      {
        label: $m.all,
        id: $m.keys.all.value,
      },
      {
        label: $m.crm_communications,
        id: $m.keys.crm_communications.value,
      },
      {
        label: $m.tasks,
        id: $m.keys.tasks.value,
      },
      {
        label: $m.orders_transactions,
        id: $m.keys.orders_transactions.value,
        hidden:
          entityType.value === EnumBmEntityType.lead ||
          entityType.value === EnumBmEntityType.supplier,
      },
    ]);

    const currentTabFilters = computed(() => {
      if (tabItems.value[currentTab.value].id === $m.keys.all.value) {
        return null;
      }
      if (
        tabItems.value[currentTab.value].id === $m.keys.crm_communications.value
      ) {
        return [EnumActivityLogType.communication];
      }

      if (tabItems.value[currentTab.value].id === $m.keys.tasks.value) {
        return [
          EnumActivityLogType.taskCompleted,
          EnumActivityLogType.taskCreated,
          EnumActivityLogType.taskCancelled,
        ];
      }

      return [
        EnumActivityLogType.orderCancelled,
        EnumActivityLogType.orderCompleted,
        EnumActivityLogType.orderCreated,
        EnumActivityLogType.creditCancelled,
        EnumActivityLogType.creditCompleted,
        EnumActivityLogType.creditCreated,
      ];
    });

    const refresh = async () => {
      if (entityType.value) {
        const activityLogFilter = new ActivityLogFilter();
        activityLogFilter.activityOnEntityTypes = [entityType.value];
        activityLogFilter.activityOnEntityIds = [props.entityId];
        activityLogFilter.logTypes = currentTabFilters.value;
        activityLogFilter.maximumResults = chunkCount.value * chunkSize;

        // Activity Logs are sorted by the BE
        activityLogs.value = await activityLogService
          .getActivityLogEntriesByFilter(api.tenantId, activityLogFilter)
          .then((val) => {
            // Have we loaded all of our entires yet?
            moreEntriesToLoad.value =
              val.matchingCount > chunkSize * chunkCount.value;

            return Object.freeze(mapActivityLogEntries(val.activityLogEntries));
          });
      }
    };

    watch(() => props.entityId, wrapPromiseWithErrorHandling(refresh), {
      immediate: true,
    });

    watch(
      [chunkCount, currentTab],
      wrapPromiseWithErrorHandling(withLoading(refresh))
    );

    return {
      currentTab,
      tabItems,
      activityLogs,
      moreEntriesToLoad,
      chunkCount,
      refresh,
    };
  },
});
</script>
